import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Smartphone, Cloud, Zap, WifiOff } from 'lucide-react';
import { useI18n } from '@/lib/i18n';

export const Mobile = () => {
  const { t } = useI18n();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const features = [
    { icon: Smartphone, label: t('mobile.responsive') },
    { icon: Cloud, label: t('mobile.cloudSync') },
    { icon: Zap, label: t('mobile.offline') },
  ];

  return (
    <section
      ref={ref}
      className="py-24 lg:py-32 bg-white"
      data-testid="mobile-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            className="order-2 lg:order-1 relative"
          >
            <div className="relative inline-block mx-auto lg:mx-0">
              <div className="relative">
                <img
                  src="https://images.pexels.com/photos/3781932/pexels-photo-3781932.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Farmer using tablet in field"
                  className="rounded-3xl shadow-elevated w-full max-w-sm mx-auto object-cover"
                  data-testid="mobile-image"
                />
                {/* Overlay gradient */}
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-t from-primary/30 to-transparent pointer-events-none" />
              </div>

              {/* Floating badge */}
              <motion.div
                initial={{ opacity: 0, y: 20, x: 20 }}
                animate={isInView ? { opacity: 1, y: 0, x: 0 } : {}}
                transition={{ delay: 0.5, duration: 0.5 }}
                className="absolute -top-4 -right-4 bg-white p-4 rounded-xl shadow-elevated border border-primary/10 hidden md:flex items-center gap-3"
                data-testid="mobile-badge"
              >
                <div className="w-10 h-10 bg-forest-100 rounded-lg flex items-center justify-center">
                  <WifiOff className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                    {t('mobile.offlineSync')}
                  </p>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            className="order-1 lg:order-2 space-y-6"
          >
            <h2
              className="text-3xl sm:text-4xl font-black text-primary tracking-tight"
              data-testid="mobile-title"
            >
              {t('mobile.title')}
            </h2>
            <p
              className="text-lg text-muted-foreground leading-relaxed"
              data-testid="mobile-subtitle"
            >
              {t('mobile.subtitle')}
            </p>

            <ul className="space-y-4 pt-4">
              {features.map((feature, index) => (
                <motion.li
                  key={feature.label}
                  initial={{ opacity: 0, x: 20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.3 + index * 0.1, duration: 0.4 }}
                  className="flex items-center gap-4"
                  data-testid={`mobile-feature-${index}`}
                >
                  <div className="w-10 h-10 bg-forest-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-5 h-5 text-forest-700" />
                  </div>
                  <span className="font-semibold text-primary">
                    {feature.label}
                  </span>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
